import { InfoBox } from '@react-google-maps/api';
import classNames from 'classnames';
import { FC, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'src/app/hooks';
import { DateFormats } from 'src/constants/dateFormats';
import { Role } from 'src/store/models/Auth';
import { ListTicket, TicketStatus } from 'src/store/models/Ticket';
import { formatDate } from 'src/utils/dateFormatter';
import useEventListener from 'src/utils/useEventListener';
import useMountTransition from 'src/utils/useMountTransition';

import classes from './MarkerInfo.module.scss';

export interface MarkerInfoProps {
  position: google.maps.LatLng;
  open: boolean;
  ticket: ListTicket;
  setOpen?: (open: boolean) => void;
}

const MarkerInfo: FC<MarkerInfoProps> = ({
  position,
  open,
  ticket,
  setOpen,
}) => {
  const { t } = useTranslation();
  const infoRef = useRef<InfoBox>(null);

  const pos = infoRef.current?.containerElement?.getBoundingClientRect();

  const closeMarkerInfo = () => {
    setOpen(false);
  };

  useEventListener('scroll', closeMarkerInfo);
  useEventListener('resize', closeMarkerInfo);

  const shouldRender = useMountTransition(open, 225);

  const user = useAppSelector((state) => state.auth.user);
  const isService = user?.role === Role.SERVICE;
  const status = ticket?.status;

  const cardDate =
    status === TicketStatus.TO_DO ? (
      <p>
        {`${t('common:assigned')}: `}
        <span>
          {formatDate(ticket?.assignedAt, DateFormats.TICKET_LIST_FORMAT)}
        </span>
      </p>
    ) : status === TicketStatus.IN_PROGRESS ? (
      <p>
        {`${t('common:started')}: `}
        <span>
          {formatDate(ticket?.startedAt, DateFormats.TICKET_LIST_FORMAT)}
        </span>
      </p>
    ) : (
      <p>
        {`${t('common:resolved')}: `}
        <span>
          {formatDate(ticket?.resolvedAt, DateFormats.TICKET_LIST_FORMAT)}
        </span>
      </p>
    );

  const serviceInfoCard = (
    <div className={classes.InfoCard}>
      <p>{ticket?.ticketId}</p>
      <div />
      <p>
        {`${t('common:status')}: `}
        <span>{t(`common:${ticket?.status}`)}</span>
      </p>
      <p>
        {`${t('common:address')}: `}
        <span>{ticket?.address}</span>
      </p>
      <p>
        {`${t('common:device')}: `}
        <span>{ticket?.model}</span>
      </p>
    </div>
  );

  const unassignedInfoCard = (
    <div className={classes.InfoCard}>
      <p>{t('common:unassigned')}</p>
      <div />
      <p>
        {`${t('common:device')}: `}
        <span>{ticket?.model}</span>
      </p>
      <p>
        {`${t('common:address')}: `}
        <span>{ticket?.address}</span>
      </p>
      <p>
        {`${t('common:reported')}: `}
        <span>
          {formatDate(ticket?.createdAt, DateFormats.TICKET_LIST_FORMAT)}
        </span>
      </p>
    </div>
  );

  const manufacturerInfoCard = (
    <div className={classes.InfoCard}>
      <p>{ticket?.ticketId}</p>
      <div />
      <p>
        {`${t('common:status')}: `}
        <span>{t(`common:${ticket?.status}`)}</span>
      </p>
      <p>
        {`${t('common:assignee')}: `}
        <span>{`${ticket?.assignee?.title}, ${ticket?.assignee?.company?.title}`}</span>
      </p>
      {cardDate}
    </div>
  );

  return (
    <>
      <InfoBox
        options={{
          closeBoxURL: '',
          enableEventPropagation: true,
          visible: false,
          boxClass: classes.MarkerInfo,
          disableAutoPan: true,
          position: position,
        }}
        ref={infoRef}
      >
        {isService
          ? serviceInfoCard
          : status === TicketStatus.UNASSIGNED
          ? unassignedInfoCard
          : manufacturerInfoCard}
      </InfoBox>
      {open || shouldRender
        ? createPortal(
            <div
              className={classNames(classes.MarkerInfo, {
                [classes.TicketIn]: open && shouldRender,
              })}
              style={{
                position: 'fixed',
                top: pos?.top - 8,
                left: pos?.left - 8,
              }}
            >
              {isService
                ? serviceInfoCard
                : status === TicketStatus.UNASSIGNED
                ? unassignedInfoCard
                : manufacturerInfoCard}
            </div>,
            document.body
          )
        : null}
    </>
  );
};

export default MarkerInfo;
